const LOCKED_STATES = new Set(['cerrado', 'rendido', 'liquidado']);
const OPEN_STATES = new Set(['en_curso', 'abierto']);

export const RETIRO_BLOQUEADO_MENSAJE =
  'El retiro/devolucion debe generarse desde el modulo formal de Retiros o Emision de Guia';

const norm = value => String(value || '').trim().toLowerCase();

export function isRepartoLocked(estado) {
  return LOCKED_STATES.has(norm(estado));
}

/**
 * Which buttons the reparto detail may enable for a given reparto state.
 * `can(code)` answers whether the current user holds a permission.
 */
export function getRepartoActions(estado, can = () => true, { rendiciones = [], liquidaciones = [] } = {}) {
  const state = norm(estado);
  const open = OPEN_STATES.has(state);
  const hasPending = (list) => list.some(item => norm(item?.estado) === 'pendiente');
  return {
    locked: LOCKED_STATES.has(state),
    iniciar: state === 'borrador' && can('REPARTOS_EDITAR'),
    entregar: open && can('REPARTOS_REGISTRAR_ENTREGA'),
    retener: open && can('REPARTOS_REGISTRAR_NOVEDAD'),
    cerrar: open && can('REPARTOS_CERRAR'),
    rendir: (state === 'cerrado' || state === 'rendicion_parcial') && can('REPARTOS_RENDIR'),
    aprobar: state === 'rendicion_parcial' && hasPending(rendiciones) && can('REPARTOS_RENDIR'),
    liquidar: state === 'rendido' && can('REPARTOS_LIQUIDAR'),
    pagar: hasPending(liquidaciones) && can('REPARTOS_LIQUIDAR'),
  };
}

export function canActOnGuia(guiaEstado) {
  return ['asignada', 'reprogramada'].includes(norm(guiaEstado));
}

/** Why a guide action button is disabled, shown as its tooltip; undefined when it is available. */
export function guiaActionBlockReason({ guiaEstado, repartoEstado, permission, can = () => true }) {
  if (!canActOnGuia(guiaEstado)) return `La guía ya está ${norm(guiaEstado) || 'procesada'}.`;
  if (!OPEN_STATES.has(norm(repartoEstado))) return `El reparto está ${norm(repartoEstado) || 'sin estado'}.`;
  if (!can(permission)) return `Falta el permiso ${permission}.`;
  return undefined;
}

const COBRO_BLOQUEOS = {
  SIN_FACTURA: 'El comprobante no se encuentra facturado.',
  COBRADA: 'La factura correspondiente ya se encuentra cobrada.',
  FACTURA_NO_ENCONTRADA: 'No se encuentra la factura correspondiente.',
  NO_DISPONIBLE: 'No se pudo consultar el saldo a cobrar.',
};

const money = value => `$ ${Number(value || 0).toFixed(2)}`;

/**
 * Whether a contado destino guide can be collected from the reparto (Clipper ST869 COBROS869).
 * `motivo` blocks the action; each entry of `avisos` asks the operator to confirm before going on.
 */
export function contadoCobroCheck(guia, sucursalActiva) {
  if (!guia?.cobro_estado) return { visible: false, puedeCobrar: false, motivo: undefined, avisos: [] };
  const bloqueo = COBRO_BLOQUEOS[guia.cobro_estado]
    || (String(guia.factura_sucursal_id) !== String(sucursalActiva?.id) ? 'La factura no pertenece a la sucursal actual.' : undefined);
  if (bloqueo) return { visible: true, puedeCobrar: false, motivo: bloqueo, avisos: [] };

  const avisos = [];
  if (guia.sucursal_responsable_id && String(guia.sucursal_responsable_id) !== String(sucursalActiva?.id)) {
    const responsable = guia.sucursal_responsable_nombre || `#${guia.sucursal_responsable_id}`;
    avisos.push(`La responsabilidad de cobro es de ${responsable} y la minuta de cobro es de ${sucursalActiva?.nombre || `#${sucursalActiva?.id}`}.`);
  }
  if (Number(guia.contrarreembolso_pendiente) > 0) {
    avisos.push(`Contrarreembolso pendiente de ingreso por ${money(guia.contrarreembolso_pendiente)}.`);
  }
  return { visible: true, puedeCobrar: true, motivo: undefined, avisos };
}

/** Delivering an uncollected contado destino guide is allowed; the operator only gets warned. */
export function entregaSinCobrarAviso(guia) {
  if (!['PENDIENTE', 'SIN_FACTURA', 'FACTURA_NO_ENCONTRADA'].includes(guia?.cobro_estado) || !(Number(guia.a_cobrar) > 0)) return '';
  return `Está por entregar una guía contado en destino sin cobrar (${money(guia.a_cobrar)}). Puede continuar de todos modos.`;
}

/** Guides billed on the same invoice all show its balance, so each invoice counts once in the total. */
export function totalContadoDestino(guias = []) {
  const facturas = new Set();
  return guias.reduce((sum, guia) => {
    if (guia.factura_id) {
      if (facturas.has(guia.factura_id)) return sum;
      facturas.add(guia.factura_id);
    }
    return sum + Number(guia.a_cobrar || 0);
  }, 0);
}

/** What the operator confirms before closing a reparto, which cannot be undone. */
export function closeRepartoSummary(guias = []) {
  const total = guias.length;
  const pendientes = guias.filter(guia => canActOnGuia(guia?.estado)).length;
  const estado = pendientes
    ? `Quedan ${pendientes} de ${total} guías sin entregar ni retener.`
    : total === 1 ? 'La guía está resuelta.' : `Las ${total} guías están resueltas.`;
  return { total, pendientes, message: `${estado} Cerrar el reparto no se puede deshacer.` };
}

/** Guides can be added until the reparto is closed, matching the API (assertRepartoOperable). */
export function canAddGuias(estado, can = () => true) {
  return !isRepartoLocked(estado) && can('REPARTOS_ASIGNAR_GUIAS');
}

/** Driver list filter for a reparto: the active drivers of its branch. */
export function choferesFilter(sucursalId) {
  return sucursalId ? { sucursalId: String(sucursalId), estados: ['ACTIVO'] } : null;
}
